import { MutableRefObject, useEffect } from 'react';

import { privateApi } from 'api';
import { handleUnauthorized, injectAuthHeader } from 'interceptors/auth';

/**
 * Parameters for configuring axios interceptors.
 * @interface UseAxiosInterceptorsParams
 */
interface UseAxiosInterceptorsParams {
  /**
   * A mutable ref object holding the current access token string or undefined.
   * This ref is used to attach the access token to outgoing axios requests.
   * @type {MutableRefObject<string | undefined>}
   */
  accessTokenRef: MutableRefObject<string | undefined>;

  /**
   * Function to update the access token in context or state.
   * Called when a new token is fetched via the refresh mechanism.
   * @param {string | undefined} token - The new access token or undefined to clear it.
   * @returns {void}
   */
  setAccessToken: (token: string | undefined) => void;

  /**
   * Async function to refresh the access token.
   * Should return a new access token string or null if refresh fails.
   * @returns {Promise<string | null>} Promise resolving to the new access token or null.
   */
  refresh: (requestId?: string) => Promise<string | null>;
}

/**
 * Custom React hook to set up axios interceptors for authorization.
 *
 * Injects the access token into outgoing requests, automatically attempts token refresh on 401 Unauthorized responses,
 * retries failed requests after successful token refresh, and converts API errors into the ApiError class.
 *
 * Interceptors are registered on mount and cleaned up on unmount or dependency change.
 *
 * @param {UseAxiosInterceptorsParams} params - The parameters for configuring interceptors.
 * @param {MutableRefObject<string | undefined>} params.accessTokenRef - Ref holding current access token.
 * @param {(token: string | undefined) => void} params.setAccessToken - Function to update the access token.
 * @param {() => Promise<string | null>} params.refresh - Function to refresh the access token.
 * @returns {void}
 */
export function useAxiosInterceptors({ accessTokenRef, setAccessToken, refresh }: UseAxiosInterceptorsParams) {
  // setup axios interceptors to handle authorization and automatic token refreshing
  useEffect(() => {
    // setup axios request interceptor to include the access token
    const requestInterceptorId = privateApi.interceptors.request.use(
      (config) => injectAuthHeader(config, accessTokenRef.current),
      (error) => Promise.reject(error),
    );

    // setup axios response interceptor to refresh the access token when it expires
    const responseInterceptorId = privateApi.interceptors.response.use(
      (response) => response,
      async (error) => handleUnauthorized(error, refresh, (request) => privateApi(request)),
    );

    // cleanup function to remove interceptors when the component unmounts or access token changes
    return () => {
      privateApi.interceptors.request.eject(requestInterceptorId);
      privateApi.interceptors.response.eject(responseInterceptorId);
    };
  }, [accessTokenRef, refresh, setAccessToken]);
}

export default useAxiosInterceptors;
